import { requestData, requestPage, type Page, type PageQuery } from '@/lib/api-client';
import type {
  AddStaffInput,
  Closure,
  License,
  LicenseInput,
  OpeningWindow,
  OpenNow,
  OperatingStatus,
  Pharmacy,
  PharmacyMetrics,
  PharmacyInput,
  PharmacyListRow,
  PharmacyStaffRole,
  StaffMember,
  StaffStatus,
  VerifyLicenseInput,
  Uuid,
} from './types';

export type PharmacyQuery = PageQuery & {
  q?: string;
  city?: string;
  state?: string;
  operatingStatus?: OperatingStatus;
  acceptsOnlineOrders?: boolean;
  supportsColdChain?: boolean;
  includeArchived?: boolean;
};

/** The backend parses these as the literal strings "true"/"false". */
const flag = (value: boolean | undefined) => (value === undefined ? undefined : String(value));

export function listPharmacies(query: PharmacyQuery = {}): Promise<Page<PharmacyListRow>> {
  const { acceptsOnlineOrders, supportsColdChain, includeArchived, ...rest } = query;
  return requestPage<PharmacyListRow>('/pharmacies', {
    query: {
      ...rest,
      acceptsOnlineOrders: flag(acceptsOnlineOrders),
      supportsColdChain: flag(supportsColdChain),
      includeArchived: flag(includeArchived),
    },
  });
}

export function getPharmacy(pharmacyId: Uuid): Promise<Pharmacy> {
  return requestData<Pharmacy>(`/pharmacies/${pharmacyId}`);
}

export function createPharmacy(body: PharmacyInput): Promise<Pharmacy> {
  return requestData<Pharmacy>('/pharmacies', { method: 'POST', body });
}

/** Identity, address and tax details. Gated on `pharmacy:write` — platform only. */
export function updatePharmacy(pharmacyId: Uuid, body: Partial<PharmacyInput>): Promise<Pharmacy> {
  return requestData<Pharmacy>(`/pharmacies/${pharmacyId}`, { method: 'PATCH', body });
}

/**
 * The operational switches, on `pharmacy:operations:write` so a pharmacy can
 * flip its own without also being able to edit the GSTIN its licence was
 * verified against. The backend refuses OPEN or online orders unless a verified,
 * unexpired licence exists.
 */
export function setOperatingStatus(
  pharmacyId: Uuid,
  body: {
    operatingStatus: OperatingStatus;
    acceptsOnlineOrders?: boolean;
    acceptsCod?: boolean;
    reason?: string;
  },
): Promise<Pharmacy> {
  return requestData<Pharmacy>(`/pharmacies/${pharmacyId}/operating-status`, {
    method: 'PATCH',
    body,
  });
}

/** Archives, never deletes — orders, settlements and ledger rows still point here. */
export function archivePharmacy(pharmacyId: Uuid): Promise<Pharmacy> {
  return requestData<Pharmacy>(`/pharmacies/${pharmacyId}`, { method: 'DELETE' });
}

export function listLicenses(pharmacyId: Uuid): Promise<License[]> {
  return requestData<License[]>(`/pharmacies/${pharmacyId}/licenses`);
}

/**
 * Puts the scan in storage and answers with its key.
 *
 * Upload then submit, in two calls, exactly as medicine images and
 * prescriptions do: one multipart endpoint doing both would orphan a file in
 * the bucket every time the licence details were refused.
 */
export function uploadLicenseDocument(
  pharmacyId: Uuid,
  file: File,
): Promise<{ key: string; contentType: string; size: number }> {
  const body = new FormData();
  body.append('file', file);
  // Multipart: the client must NOT set content-type, or the boundary is lost.
  return requestData<{ key: string; contentType: string; size: number }>(
    `/pharmacies/${pharmacyId}/licenses/upload`,
    { method: 'POST', body },
  );
}

export function submitLicense(pharmacyId: Uuid, body: LicenseInput): Promise<License> {
  return requestData<License>(`/pharmacies/${pharmacyId}/licenses`, { method: 'POST', body });
}

/**
 * The decision that admits a pharmacy to the platform, which is why it has its
 * own permission. Rejecting or suspending the last valid licence closes the
 * pharmacy in the same transaction — the response says whether it did.
 */
export function verifyLicense(
  pharmacyId: Uuid,
  licenseId: Uuid,
  body: VerifyLicenseInput,
): Promise<License & { pharmacyClosed: boolean }> {
  return requestData<License & { pharmacyClosed: boolean }>(
    `/pharmacies/${pharmacyId}/licenses/${licenseId}/verify`,
    { method: 'POST', body },
  );
}

export function listStaff(pharmacyId: Uuid): Promise<StaffMember[]> {
  return requestData<StaffMember[]>(`/pharmacies/${pharmacyId}/staff`);
}

/**
 * Adding someone is `pharmacy:staff:manage` — admin only. The roster row is
 * what grants access to this pharmacy's stock and money, so who is on it is a
 * platform decision, not the shop's.
 */
export function addStaff(pharmacyId: Uuid, body: AddStaffInput): Promise<StaffMember> {
  return requestData<StaffMember>(`/pharmacies/${pharmacyId}/staff`, { method: 'POST', body });
}

export function updateStaff(
  pharmacyId: Uuid,
  staffId: Uuid,
  body: { role?: PharmacyStaffRole; status?: StaffStatus },
): Promise<StaffMember> {
  return requestData<StaffMember>(`/pharmacies/${pharmacyId}/staff/${staffId}`, {
    method: 'PATCH',
    body,
  });
}


// ── opening hours ───────────────────────────────────────────────────────────

export function listHours(pharmacyId: Uuid): Promise<OpeningWindow[]> {
  return requestData<OpeningWindow[]>(`/pharmacies/${pharmacyId}/hours`);
}

/** The whole week is replaced at once — see the service for why. */
export function replaceHours(pharmacyId: Uuid, hours: OpeningWindow[]): Promise<OpeningWindow[]> {
  return requestData<OpeningWindow[]>(`/pharmacies/${pharmacyId}/hours`, {
    method: 'PUT',
    body: { hours: hours.map(({ dayOfWeek, opensAt, closesAt }) => ({ dayOfWeek, opensAt, closesAt })) },
  });
}

export function getOpenNow(pharmacyId: Uuid): Promise<OpenNow> {
  return requestData<OpenNow>(`/pharmacies/${pharmacyId}/open-now`);
}

// ── ad-hoc closures ─────────────────────────────────────────────────────────

export function listClosures(pharmacyId: Uuid): Promise<Closure[]> {
  return requestData<Closure[]>(`/pharmacies/${pharmacyId}/closures`);
}

export function createClosure(
  pharmacyId: Uuid,
  body: { startsAt: string; endsAt: string; reason: string },
): Promise<Closure> {
  return requestData<Closure>(`/pharmacies/${pharmacyId}/closures`, { method: 'POST', body });
}

export function deleteClosure(pharmacyId: Uuid, closureId: Uuid): Promise<void> {
  return requestData<void>(`/pharmacies/${pharmacyId}/closures/${closureId}`, { method: 'DELETE' });
}

// ── reliability metrics ─────────────────────────────────────────────────────

export function getMetrics(pharmacyId: Uuid, days = 30): Promise<PharmacyMetrics> {
  return requestData<PharmacyMetrics>(`/pharmacies/${pharmacyId}/metrics`, { query: { days } });
}
